<template>
    <div class="app-asset-manager--ppt-templates app-asset-manager grid">

        <div v-show="status"
            :class="['status', { 'status--show': status }]">
            <div class="status-message">{{ status }}</div>
            <div v-show="uploadPercentage"
                class="element-progress">
                <p class="element-progress__text">{{ uploadPercentage }}%</p>
                <div class="element-progress__bar">
                    <div
                        class="element-progress__bar-filled"
                        role="progressbar"
                        aria-valuenow="0"
                        aria-valuemin="0"
                        aria-valuemax="100"
                        :style="`width: ${uploadPercentage}%;`">
                    </div>
                </div>
            </div>
        </div>

        <h4 class="app-asset-manager__lite-section-title">Upload</h4>
            
        <app-error
            v-if="hasErrors"
            class="is--inline"
            v-bind="{ errors, message: errorMessage }" />

        <file-pond
            name="asset"
            ref="asset-upload"
            class-name="app-asset-manager__asset-upload"
            credits="false"
            allow-multiple="true"
            allow-revert="true"
            allow-process="false"
            allow-image-preview="false"
            label-tap-to-undo="press to dismiss"
            label-idle='<span class="filepond--label-action">Click to upload</span> or drag and drop <span class="filepond--filetypes"> PPTX</span>'
            :max-file-size="maxFileSize"
            :accepted-file-types="mimeTypes"
            :file-validate-type-detect-type="validateFileType"
            :server="serverConfig"
            @processfile="onFileProcess" />

        <h4 class="app-asset-manager__lite-section-title">Uploaded Templates</h4>
        <div class="element-table__wrapper">
            <table>
                <thead>
                    <tr>
                        <th @click="setSort('name')">Name</th>
                        <th v-if="uploadDate"  @click="setSort('updated_at')">Uploaded Date</th>
                        <th v-if="typeDropdown" >Type</th>
                        <th></th>
                    </tr>
                </thead>
                <tbody>
                    <tr v-for="(file, index) in filteredFiles">
                        <td class="element-table__td--bold">{{ file.name }}</td>
                        <td v-if="uploadDate">{{ localizeDate(file.updated_at).format('MMM D, YYYY h:mm A') }}</td>
                        <td class="element-table__actions">

                            <div @click="$event.isAction = true" class="dropdown">
                                <button class="element-table__actions-button element-button--icon element-button" type="button" data-bs-toggle="dropdown">
                                    <svg class="element-icon--more-info element-icon" width="1em" height="1em"><use href="/img/icons.svg#icon-more-info"></use></svg>
                                </button>

                                <div class="app-dropdown dropdown-menu dropdown-menu-right">
                                    <a
                                        :title="file.url"
                                        :href="file.url"
                                        target="_blank">
                                        <svg class="element-icon--download element-icon" width="1em" height="1em"><use href="/img/icons.svg#icon-download"></use></svg>
                                        Download
                                    </a>
                                    <button
                                        class="is--alert"
                                        title="Delete Asset"
                                        @click="deleteFile(file)">
                                        <svg class="element-icon--delete element-icon" width="1em" height="1em"><use href="/img/icons.svg#icon-delete"></use></svg>
                                        Delete
                                    </button>
                                </div>
                            </div>
                        </td>
                    </tr>
                </tbody>
            </table>
            <p v-if="!loaded">Loading, please wait...</p>
            <p v-else-if="!filteredFiles.length"
                class="app-asset-manager__empty empty-table element-empty-instructions">
                No template found.
            </p>
        </div>
    </div>
</template>

<script>
import _ from 'lodash'
import { mapState, mapGetters } from 'vuex'
import { localizeDate } from '../helpers/util'
import FileUploader from './FileUploader.vue'
import AssetManagerFolder from './AssetManagerFolder.vue'
import vueFilePond from 'vue-filepond'
import FilePondPluginFileValidateType from 'filepond-plugin-file-validate-type'
import FilePondPluginFileValidateSize from 'filepond-plugin-file-validate-size'
import FilePondPluginImagePreview from 'filepond-plugin-image-preview'
import { classifyUploadError, parseUploadResponseError } from '../helpers/uploadErrorClassifier'

// styles
import 'filepond/dist/filepond.css'
import 'filepond-plugin-image-preview/dist/filepond-plugin-image-preview.css'

// Create FilePond component
const FilePond = vueFilePond(
    FilePondPluginFileValidateType,
    FilePondPluginFileValidateSize,
    FilePondPluginImagePreview
)

export default {
    components: {
        AssetManagerFolder,
        FileUploader,
        FilePond
    },
    props: {
        files: {
            type: Array,
            default: () => []
        },
        assetFiles: {
            type: Array,
            default: () => []
        },
        maxFileSize: {
            type: Number,
            default: (50 * 1024 * 1024) // 50MB
        },
        source: {
            type: String
        },
        validExtensions: {
            type: Object,
            default: () => { }
        },
        validMimeTypes: {
            type: Object,
            default: () => { }
        },
        uploadDate: {
            type: Boolean,
            default: true
        },
        typeDropdown: {
            type: Boolean,
            default: false
        },
        autoLoad: {
            type: Boolean,
            default: true
        }
    },
    data () {
        return {
            status: null,
            errors: false,
            errorMessage: null,
            tooltipHoverState: null,
            uploadPercentage: 0,
            sortKey: 'name',
            sortOrder: 'asc'
        }
    },
    computed: {
        ...mapState('assets', {
            ready: state => state.ready,
            loaded: state => state.loaded,
            options: state => state.options,
            selectedId: state => state.selectedId,
            filesLoading: state => state.filesLoading
        }),
        ...mapGetters('assets', {
            allFiles: 'fileSet',
            file: 'selectedFile',
            metadata: 'selectedMetadata'
        }),
        hasErrors () {
            return this.errorMessage
        },
        serverConfig () {
            return {
                fetch: null,
                revert: null,
                process: {
                    url: this.uploadUrl,
                    onerror: this.onServerResponseError,
                },
                headers: {
                    'Accept': 'application/json',
                    'X-Requested-With': 'XMLHttpRequest',
                    'X-CSRF-TOKEN': this.$store.state.csrfToken
                }
            }
        },
        uploadUrl () {
            return this.source
        },
        mode () {
            return _.get(this.options, 'assetType')
        },
        fileUrl () {
            const timestamp = _.get(this.file, 'timestamp', 0)
            return `${this.file?.url}?t=${timestamp}`
        },
        extension () {
            return /(?:\.([^.]+))?$/.exec(this.file?.name)[1]
        },
        extensions () {
            if (this.mode) {
                return this.getExtensions(this.mode)
            }
    
            const list = _.reduce(this.validExtensions, (result, extensions) => {
                const formattedExt = _.map(extensions, (ext) => `.${ext}`)
                return result.concat(formattedExt)
            }, [])

            return _.uniq(list)
        },

        mimeTypes () {
            if (this.mode) {
                let _result = [];
                if (_.isArray(this.mode))
                {
                    const _all = this.validMimeTypes;
                    _(this.mode).each(function(_type) {
                        _result = _.union(_result, _.get(_all, _type, []))
                    });
                    return _result
                }
                else
                {
                    return _.get(this.validMimeTypes, this.mode, [])
                }
            }
    
            return _.reduce(this.validMimeTypes, (result, types) => result.concat(types), [])
        },
        filteredFiles () {
            let filtered = this.allFiles ;
            if (this.sortKey) {
                // TODO isnt working
                return _.orderBy(
                    filtered,
                    [file => {
                        if (this.sortKey === 'updated_at') {
                            return new Date(file.updated_at).getTime() || 0;
                        }
                        if (this.sortKey === 'name') {
                            return (file.name || '').toLowerCase();
                        }
                        return file[this.sortKey];
                    }],
                    [this.sortOrder]
                );
            }
            return filtered;
        },
        types () {
            const types = this.mode ? (_.isArray(this.mode) ? this.mode : [this.mode]) : _.keys(this.validExtensions)
            return types.sort()
        },
        root () {
            return { files: this.filteredFiles }
        },
        prerequisitesMet () {
        return this.allFiles?.length > 0
        },
    },
    methods: {
        localizeDate,
        setSort(key) {
            console.log('setSort', key)
            if (this.sortKey === key) {
                this.sortOrder = this.sortOrder === 'asc' ? 'desc' : 'asc';
            } else {
                this.sortKey = key;
                this.sortOrder = 'asc';
            }
            console.log(this.filteredFiles())
        },
        handleFilePondInit () {
            // console.log('FilePond has initialized')
        },
        validateFileType: (source, type) => new Promise((resolve) => {
            resolve(type)
        }),
        getExtensions (type) {
            let extensions = [];
            if (_.isArray(type))
            {
                const _all = this.validExtensions;
                _(type).each(function(_type) {
                    extensions = _.union(extensions, _.get(_all, _type))
                });
            }
            else
            {
                extensions = _.get(this.validExtensions, type)
            }

            return _.map(extensions, (ext) => `.${ext}`)
        },
        setStatus (message, progress = 0) {
            this.status = message
            this.uploadPercentage = progress
        },
        onFileUploadStart () {
            this.errors = false
        },
        onFileUploadProgress ({ loaded, total }) {
            // this.setStatus('Uploading...', Math.round(loaded / total * 100))
        },
        // onerror in serverConfig: only returns the label string for FilePond's inline file-item UI
        onServerResponseError(response) {
            return parseUploadResponseError(response)
        },
        onFileUploadFail (errors) {
            console.log(errors)
            this.errorMessage = errors?.message || 'An error occurred while uploading the file.'
        },
        onFileProcess (error, data) {
            // if error occurs, display parsed response message or fallback to generic message per status type
            if (error) {
                this.onFileUploadFail({ message: error?.body || classifyUploadError(error?.code) })
                return
            }

            try {
                const response = JSON.parse(data.serverId)
                const file = _.get(response, 'data')
                this.onFileUploadSuccess(file)
            } catch (err) {
                console.error(err)
            }
        },
        onTooltipHover(label) {
            this.tooltipHoverState = label
        },
        onTooltipLeave(label) {
            if (this.tooltipHoverState === label)
                this.tooltipHoverState = null
        },
        isTooltipHovered(label) {
            return this.tooltipHoverState === label
        },
        async onUpdateAiMetadata (file) {
            if (file)
                await this.$store.dispatch('assets/updateFile', { id: file.id, ai_ingestion_type: (file.ai_ingestion_type ? 1 : 0) })

            this.setStatus(null)
        },
        async onFileUploadSuccess (file) {
            // select file
            if (file) {
                this.errors = false
                this.errorMessage = null
                this.$store.commit('assets/addFile', file)
                console.log('selecting file', file)
                await this.$store.dispatch('assets/selectFile', file.id)
            }

            this.setStatus(null)
        },
        async deleteFile (file) {
            if (!confirm(`Are you sure you want to delete [${file.name}]?`)) {
                return
            }
            
            // this.setStatus(`Deleting [${file.name}]`)

            const result = await this.$store.dispatch('assets/deleteFile', file.id)
            if (result) {
                this.$store.commit('assets/setSelectedId', null)
            }

            this.setStatus(null)
        }
    },
    async created () {
        this.$store.commit('assets/setSource', this.source)
        this.$store.commit('assets/setFiles', this.files)

        if (this.autoLoad) {
            if (!this.files.length) {
                await this.$store.dispatch('assets/update')
            } else {
                this.$store.commit('assets/setLoaded', true)
            }
        }

        this.$store.commit('assets/setReady', true)
    }
}
</script>
